import axios from 'axios';

// Determine API URL based on environment
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_URL = isLocal 
    ? 'http://localhost:8000/api' 
    : 'https://biz.appy.pk/api';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Add token to requests
api.interceptors.request.use(config => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getSalesOverTime = (period) => api.get('/dashboard/sales-over-time', { params: { period } });
export const getTopProducts = () => api.get('/dashboard/top-products');
export const getPaymentMethods = () => api.get('/dashboard/payment-methods');
export const getRecentActivities = () => api.get('/dashboard/recent-activities');

// Reports
export const getReports = (params) => api.get('/reports', { params });
export const getSoldProducts = (params) => api.get('/reports/sold-products', { params });

// Global Search
export const globalSearch = (query) => api.get('/search', { params: { query } });

export const login = (email, password) => api.post('/login', { email, password });
export const register = (data) => api.post('/register', data);
export const getUser = () => api.get('/user');
export const changePassword = (data) => api.post('/change-password', data);
export const getActivityLog = () => api.get('/activity-log');
export const getSessions = () => api.get('/user/sessions');
export const terminateSession = (id) => api.delete(`/user/sessions/${id}`);
export const terminateAllSessions = () => api.delete('/user/sessions');
export const getSecurityLogs = (page = 1) => api.get('/user/security-logs', { params: { page } });
export const submitSupportTicket = (data) => api.post('/user/support', data);


// Profile Updates
export const updateProfile = (data) => api.post('/user/profile', data);
export const updateAvatar = (formData) => api.post('/user/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
});

// 2FA Management
export const generate2FASecret = () => api.post('/user/2fa/generate');
export const enable2FA = (data) => api.post('/user/2fa/enable', data);
export const disable2FA = (data) => api.post('/user/2fa/disable', data);
export const verify2FA = (data) => api.post('/login/2fa', data);

// Products
export const getProducts = (params) => api.get('/products', { params });
export const exportProducts = () => api.get('/products/export', { responseType: 'blob' });
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const getAlerts = () => api.get('/products/alerts');

// Categories
export const getCategories = (params) => api.get('/categories', { params });
export const createCategory = (data) => api.post('/categories', data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// Sales
export const createSale = (data) => api.post('/sales', data);
export const getSaleItems = (id) => api.get(`/sales/${id}/items`);
export const addSalePayment = (id, data) => api.post(`/sales/${id}/payment`, data);

// Attendance
export const checkIn = (coords) => api.post('/attendance/check-in', coords);
export const checkOut = (coords) => api.post('/attendance/check-out', coords);

// Customers
export const getCustomers = (params) => api.get('/customers', { params });
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const addPayment = (id, data) => api.post(`/customers/${id}/payment`, data);
export const getCustomerHistory = (id) => api.get(`/customers/${id}/history`);
export const getPayments = (id) => api.get(`/customers/${id}/payments`);
export const getLedger = (id) => api.get(`/customers/${id}/ledger`);

// Suppliers
export const getSuppliers = (params) => api.get('/suppliers', { params });
export const createSupplier = (data) => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`);

// Expenses
export const getExpenses = (params) => api.get('/expenses', { params });
export const createExpense = (data) => api.post('/expenses', data);
export const deleteExpense = (id) => api.delete(`/expenses/${id}`);

// Returns
export const getReturns = (params) => api.get('/returns', { params });
export const createReturn = (data) => api.post('/returns', data);
export const getReturnDetails = (id) => api.get(`/returns/${id}`);

export const getInvitations = () => api.get('/invitations');
export const getTeamStats = () => api.get('/team/stats');
export const sendInvitation = (data) => api.post('/invitations', data);
export const resendInvitation = (id) => api.post(`/invitations/${id}/resend`);
export const cancelInvitation = (id) => api.delete(`/invitations/${id}`);
export const getEmployeeStats = () => api.get('/employee/stats');

// Password Reset Functions
export const forgotPassword = (email) => api.post('/password/forgot', { email });
export const verifyOTP = (email, otp) => api.post('/password/verify-otp', { email, otp });
export const resetPassword = (data) => api.post('/password/reset', data);
export const adminResetPassword = (id, data) => api.post(`/team/members/${id}/reset-password`, data);
export const superAdminResetPassword = (id, data) => api.post(`/super-admin/users/${id}/reset-password`, data);

// Settings
export const getSettings = () => api.get('/settings/public');
export const updateLogo = (formData) => api.post('/settings/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
});
export const removeLogo = () => api.delete('/settings/logo');
export const updateSetting = (data) => api.post('/settings', data);

// Employee Salary Management
export const getEmployeeSalaryList = () => api.get('/employee-salary');
export const setEmployeeSalary = (data) => api.post('/employee-salary/set', data);
export const getEmployeeAttendance = (params) => api.get('/employee-salary/attendance', { params });
export const markEmployeeAttendance = (data) => api.post('/employee-salary/attendance', data);
export const getEmployeeFines = (params) => api.get('/employee-salary/fines', { params });
export const addEmployeeFine = (data) => api.post('/employee-salary/fines', data);
export const deleteEmployeeFine = (id) => api.delete(`/employee-salary/fines/${id}`);
export const generatePayroll = (data) => api.post('/employee-salary/payroll', data);
export const markPayrollPaid = (id) => api.post(`/employee-salary/payroll/${id}/paid`);
export const getPayrollHistory = (params) => api.get('/employee-salary/payroll-history', { params });

const apiService = {
    getDashboardStats,
    getSalesOverTime,
    getTopProducts,
    getPaymentMethods,
    getReports,
    getSoldProducts,
    globalSearch,
    login,
    register,
    getUser,
    changePassword,
    getActivityLog,
    updateProfile,
    updateAvatar,
    generate2FASecret,
    enable2FA,
    disable2FA,
    verify2FA,
    getProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    getAlerts,
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    createSale,
    getSaleItems,
    addSalePayment,
    checkIn,
    checkOut,
    getCustomers,
    createCustomer,
    updateCustomer,
    addPayment,
    getCustomerHistory,
    getPayments,
    getLedger,
    getSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    getExpenses,
    createExpense,
    deleteExpense,
    getInvitations,
    sendInvitation,
    getReturns,
    createReturn,
    getReturnDetails,
    getSettings,
    updateLogo,
    removeLogo,
    updateSetting,
    submitSupportTicket,
    getEmployeeSalaryList,
    setEmployeeSalary,
    getEmployeeAttendance,
    markEmployeeAttendance,
    getEmployeeFines,
    addEmployeeFine,
    deleteEmployeeFine,
    generatePayroll,
    markPayrollPaid,
    getPayrollHistory,
    exportProducts,
    getRecentActivities,
    resendInvitation,
    cancelInvitation,
    getTeamStats,
    getSessions,
    terminateSession,
    terminateAllSessions,
    getSecurityLogs,
    forgotPassword,
    verifyOTP,
    resetPassword,
    adminResetPassword,
    superAdminResetPassword,

    get: (url, config) => api.get(url, config),
    post: (url, data, config) => api.post(url, data, config),
    put: (url, data, config) => api.put(url, data, config),
    delete: (url, config) => api.delete(url, config),
};

// eslint-disable-next-line import/no-anonymous-default-export
export default apiService;
